import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface WebhookEchoLog {
  id: string;
  created_at: string;
  method: string;
  path: string | null;
  headers: Record<string, any>;
  body: Record<string, any>;
  query_params: Record<string, any>;
  ip_address: string | null;
  user_agent: string | null;
}

export const useWebhookEchoLogs = (limit = 50) => {
  return useQuery({
    queryKey: ['webhook-echo-logs', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_echo_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as WebhookEchoLog[];
    },
    refetchInterval: 5000, // Auto-refresh a cada 5 segundos
  });
};

export const useClearWebhookLogs = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (olderThan?: string) => {
      let query = supabase.from('webhook_echo_logs').delete();
      
      if (olderThan) {
        query = query.lt('created_at', olderThan);
      } else {
        // Delete all (usando match vazio)
        query = query.neq('id', '00000000-0000-0000-0000-000000000000');
      }

      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-echo-logs'] });
      toast({
        title: 'Logs limpos',
        description: 'Os logs do webhook echo foram removidos com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao limpar logs',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteWebhookLog = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (logId: string) => {
      const { error } = await supabase
        .from('webhook_echo_logs')
        .delete()
        .eq('id', logId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-echo-logs'] });
      toast({
        title: 'Log deletado',
        description: 'O log foi removido com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao deletar log',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};
